import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import {
  BetaAdminActions,
  BetaAdminCard,
  BetaAdminField,
  betaAdminTextareaProps,
} from './BetaAdminUi';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

interface BetaKnownIssuesAdminProps {
  session: Session;
  onFeedback: (message: string) => void;
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
}

export default function BetaKnownIssuesAdmin({
  session,
  onFeedback,
  busy,
  onBusyChange,
}: BetaKnownIssuesAdminProps) {
  const [issuesText, setIssuesText] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/known-issues?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
      );
      if (!res.ok) throw new Error('Chargement impossible');
      const data = await res.json() as { issues?: string[] };
      setIssuesText((data.issues || []).join('\n'));
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [onFeedback, session.pseudo]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    onBusyChange(true);
    try {
      const issues = issuesText
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/known-issues?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ issues }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setIssuesText((data.issues || []).join('\n'));
      onFeedback('Problèmes connus mis à jour');
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      onBusyChange(false);
    }
  };

  const disabled = busy || loading;

  return (
    <BetaAdminCard
      icon="⚠️"
      title="Problèmes connus"
      description="Affichés dans la modal de bienvenue et Paramètres → Bêta-test."
      footer={(
        <BetaAdminActions align="end">
          <button
            type="button"
            className="beta-admin-btn beta-admin-btn--primary"
            disabled={disabled}
            onClick={() => void save()}
          >
            Enregistrer
          </button>
        </BetaAdminActions>
      )}
    >
      <BetaAdminField label="Liste" hint="Une ligne par point">
        <textarea
          {...betaAdminTextareaProps(disabled, 8)}
          value={issuesText}
          onChange={e => setIssuesText(e.target.value)}
          placeholder={'Ex. : reconnexion parfois nécessaire après une mise à jour\nEx. : le chat peut être lent le soir'}
        />
      </BetaAdminField>
    </BetaAdminCard>
  );
}
